/**
 * Audio Recorder Widget
 *
 * Integrates with Gravity Forms file upload fields.
 * Only initializes if .audio-recorder-widget exists on the page.
 */

// Configuration — form IDs come from theme (PUSH_AUDIO_FORM_IDS in functions.php).
const GRAVITY_FORM_FILE_INPUT_SELECTOR = 'input[type="file"][name*="input_"]';
const RECORD_DURATION = 30;

// RecordRTC type declarations
declare const RecordRTC: any;

// Fathom analytics type declaration
declare global {
  interface Window {
    fathom?: {
      trackEvent: (name: string, options?: { _value?: number }) => void;
    };
    pushInitAudioRecorder?: () => void;
    PUSH_AUDIO_FORM_IDS?: number[];
  }
}

const AUDIO_FORM_IDS: number[] = Array.isArray(window.PUSH_AUDIO_FORM_IDS)
  ? window.PUSH_AUDIO_FORM_IDS.map(Number).filter((id) => id > 0)
  : [];

const TEXT_REVIEW_FIELD_SELECTOR = AUDIO_FORM_IDS.map((id) => `#field_${id}_2`).join(", ");

function debugLog(...args: unknown[]): void {
  console.log("[audio-recorder]", ...args);
}

/**
 * Track an event with Fathom Analytics (if available)
 * @param eventName - Name of the event to track
 * @param value - Optional value in cents (for monetary tracking)
 */
function trackEvent(eventName: string, value?: number): void {
  if (typeof window.fathom?.trackEvent === "function") {
    if (value !== undefined) {
      window.fathom.trackEvent(eventName, { _value: value });
    } else {
      window.fathom.trackEvent(eventName);
    }
  }
}

interface RecorderState {
  recorder: any;
  stream: MediaStream | null;
  audioContext: AudioContext | null;
  analyser: AnalyserNode | null;
  animationId: number | null;
  timerInterval: number | null;
  timeRemaining: number;
  isRecording: boolean;
  audioBlob: Blob | null;
}

let recordRtcLoading: Promise<void> | null = null;

function loadRecordRtc(): Promise<void> {
  if (typeof RecordRTC !== "undefined") {
    return Promise.resolve();
  }
  if (recordRtcLoading) {
    return recordRtcLoading;
  }
  recordRtcLoading = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = "https://cdn.jsdelivr.net/npm/recordrtc@5.6.2/RecordRTC.min.js";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load RecordRTC"));
    document.head.appendChild(script);
  });
  return recordRtcLoading;
}

export function initAudioRecorder(): void {
  const widgets = document.querySelectorAll<HTMLElement>(
    ".audio-recorder-widget:not([data-arw-initialized])"
  );
  debugLog("initAudioRecorder", { widgetCount: widgets.length });
  if (!widgets.length) return;

  loadRecordRtc()
    .then(() => {
      widgets.forEach((widget) => {
        widget.setAttribute("data-arw-initialized", "true");
        setupRecorder(widget);
      });
    })
    .catch((err) => {
      console.warn("Audio recorder: RecordRTC load failed", err);
    });
}

function setupRecorder(widget: HTMLElement): void {
  const state: RecorderState = {
    recorder: null,
    stream: null,
    audioContext: null,
    analyser: null,
    animationId: null,
    timerInterval: null,
    timeRemaining: RECORD_DURATION,
    isRecording: false,
    audioBlob: null,
  };

  // Get DOM elements
  const recordBtn = widget.querySelector<HTMLButtonElement>("#arwRecordBtn");
  const timer = widget.querySelector<HTMLElement>("#arwTimer");
  const status = widget.querySelector<HTMLElement>("#arwStatus");
  const progress = widget.querySelector<HTMLElement>("#arwProgress");
  const playback = widget.querySelector<HTMLElement>("#arwPlayback");
  const audioPlayer = widget.querySelector<HTMLAudioElement>("#arwAudioPlayer");
  const reRecordBtn = widget.querySelector<HTMLButtonElement>("#arwReRecord");
  const visualizer = widget.querySelector<HTMLCanvasElement>("#arwVisualizer");
  const mainUI = widget.querySelector<HTMLElement>("#arwMain");
  const unsupportedUI = widget.querySelector<HTMLElement>("#arwUnsupported");

  if (
    !recordBtn ||
    !timer ||
    !status ||
    !progress ||
    !playback ||
    !audioPlayer ||
    !reRecordBtn ||
    !visualizer ||
    !mainUI ||
    !unsupportedUI
  ) {
    console.warn("Audio recorder: Missing required DOM elements");
    return;
  }

  const ctx = visualizer.getContext("2d");
  if (!ctx) return;

  // Check browser support
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
    mainUI.style.display = "none";
    unsupportedUI.style.display = "block";
    return;
  }

  function getFileInput(): HTMLInputElement | null {
    const form = widget.closest("form");
    if (form) {
      return form.querySelector<HTMLInputElement>(GRAVITY_FORM_FILE_INPUT_SELECTOR);
    }
    return document.querySelector<HTMLInputElement>(GRAVITY_FORM_FILE_INPUT_SELECTOR);
  }

  function resizeCanvas(): void {
    const rect = visualizer.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    visualizer.width = rect.width * dpr;
    visualizer.height = rect.height * dpr;
    ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  function getCanvasSize(): { width: number; height: number } {
    const dpr = window.devicePixelRatio || 1;
    return {
      width: visualizer.width / dpr,
      height: visualizer.height / dpr,
    };
  }

  function formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }

  function updateProgress(remaining: number): void {
    const elapsed = RECORD_DURATION - remaining;
    const percent = (elapsed / RECORD_DURATION) * 100;
    progress.style.width = percent + "%";
  }

  function drawVisualizer(): void {
    const { width, height } = getCanvasSize();

    ctx!.fillStyle = "#000";
    ctx!.fillRect(0, 0, width, height);

    if (state.analyser && state.isRecording) {
      const bufferLength = state.analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      state.analyser.getByteFrequencyData(dataArray);

      const barWidth = (width / bufferLength) * 2.5;
      let x = 0;

      for (let i = 0; i < bufferLength; i++) {
        const barHeight = (dataArray[i] / 255) * height * 0.8;
        if (barHeight > 0) {
          ctx!.fillStyle = "#E94213";
          ctx!.fillRect(x, height - barHeight, barWidth - 1, barHeight);
        }
        x += barWidth;
      }
    }

    if (state.isRecording) {
      state.animationId = requestAnimationFrame(drawVisualizer);
    }
  }

  function drawIdleVisualizer(): void {
    const { width, height } = getCanvasSize();

    ctx!.fillStyle = "#000";
    ctx!.fillRect(0, 0, width, height);
    ctx!.beginPath();
    ctx!.strokeStyle = "#e0e0e0";
    ctx!.lineWidth = 1;
    ctx!.moveTo(0, height / 2);
    ctx!.lineTo(width, height / 2);
    ctx!.stroke();
  }

  function attachToGravityForms(blob: Blob): boolean {
    const fileInput = getFileInput();

    if (!fileInput) {
      debugLog("file input not found for attach");
      console.warn(
        "Gravity Forms file input not found. Selector:",
        GRAVITY_FORM_FILE_INPUT_SELECTOR
      );
      status.textContent = "Recording ready! (Manual upload may be required)";
      status.classList.add("arw-status--success");
      return false;
    }

    const fileName = `testimonial-${Date.now()}.wav`;
    const file = new File([blob], fileName, { type: "audio/wav" });

    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(file);
    fileInput.files = dataTransfer.files;

    fileInput.dispatchEvent(new Event("change", { bubbles: true }));

    // Mark that audio was attached (used for submission tracking)
    document.body.setAttribute("data-audio-recording-attached", "true");
    debugLog("audio attached to file input", { fileName, size: blob.size });

    status.textContent = "Recording captured!";
    status.classList.remove("arw-status--error");
    status.classList.add("arw-status--success");

    return true;
  }

  async function startRecording(): Promise<void> {
    try {
      resizeCanvas();

      state.stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      // Set up audio context for visualization
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      state.audioContext = new AudioContextClass();

      if (state.audioContext.state === "suspended") {
        await state.audioContext.resume();
      }

      state.analyser = state.audioContext.createAnalyser();
      const source = state.audioContext.createMediaStreamSource(state.stream);
      source.connect(state.analyser);
      state.analyser.fftSize = 256;

      // Create RecordRTC recorder - outputs WAV
      state.recorder = new RecordRTC(state.stream, {
        type: "audio",
        mimeType: "audio/wav",
        recorderType: RecordRTC.StereoAudioRecorder,
        numberOfAudioChannels: 1,
        desiredSampRate: 44100,
      });

      state.recorder.startRecording();
      state.isRecording = true;

      // Track recording started
      trackEvent("Audio Recording Started");

      recordBtn.classList.add("arw-record-btn--recording");
      timer.classList.add("arw-timer--recording");
      progress.classList.add("arw-progress-fill--recording");
      status.textContent = "Recording...";
      status.classList.remove("arw-status--error", "arw-status--success");
      playback.classList.remove("arw-playback--visible");

      drawVisualizer();

      state.timeRemaining = RECORD_DURATION;
      timer.textContent = formatTime(state.timeRemaining);
      updateProgress(state.timeRemaining);

      state.timerInterval = window.setInterval(() => {
        state.timeRemaining--;
        timer.textContent = formatTime(state.timeRemaining);
        updateProgress(state.timeRemaining);

        if (state.timeRemaining <= 0) {
          stopRecording();
        }
      }, 1000);
    } catch (err: any) {
      console.error("Microphone error:", err);

      let errorMessage = "Unable to access microphone.";
      let errorType = "access_denied";

      if (err.name === "NotFoundError") {
        errorMessage = "Could not access microphone.";
        errorType = "not_found";
      } else if (err.name === "NotReadableError") {
        errorMessage = "Microphone is in use by another app.";
        errorType = "in_use";
      }

      // Track recording error
      trackEvent(`Audio Recording Error - ${errorType}`);

      status.textContent = errorMessage;
      status.classList.add("arw-status--error");
    }
  }

  function stopRecording(): void {
    if (state.recorder && state.isRecording) {
      state.recorder.stopRecording(() => {
        state.audioBlob = state.recorder.getBlob();
        console.log(
          "Recording complete. Type:",
          state.audioBlob?.type,
          "Size:",
          state.audioBlob?.size
        );

        if (state.audioBlob) {
          const audioUrl = URL.createObjectURL(state.audioBlob);
          audioPlayer.src = audioUrl;
          playback.classList.add("arw-playback--visible");

          // Track recording completed with duration (in seconds, passed as cents for Fathom)
          const durationRecorded = RECORD_DURATION - state.timeRemaining;
          trackEvent("Audio Recording Completed", durationRecorded * 100);

          attachToGravityForms(state.audioBlob);
        }
      });
    }

    if (state.stream) {
      state.stream.getTracks().forEach((track) => track.stop());
      state.stream = null;
    }

    if (state.audioContext) {
      state.audioContext.close();
      state.audioContext = null;
      state.analyser = null;
    }

    if (state.animationId) {
      cancelAnimationFrame(state.animationId);
      state.animationId = null;
    }

    if (state.timerInterval) {
      clearInterval(state.timerInterval);
      state.timerInterval = null;
    }

    state.isRecording = false;

    recordBtn.classList.remove("arw-record-btn--recording");
    timer.classList.remove("arw-timer--recording");
    // progress.classList.remove('arw-progress-fill--recording');

    timer.textContent = formatTime(RECORD_DURATION);
    // progress.style.width = '0%';

    drawIdleVisualizer();
  }

  function resetRecorder(): void {
    // Track re-record action
    trackEvent("Audio Recording Re-record");

    playback.classList.remove("arw-playback--visible");
    audioPlayer.src = "";
    state.audioBlob = null;
    status.textContent = "Tap to start recording";
    status.classList.remove("arw-status--error", "arw-status--success");

    // Clear the audio attached flag
    document.body.removeAttribute("data-audio-recording-attached");

    const fileInput = getFileInput();
    if (fileInput) {
      fileInput.value = "";
      fileInput.dispatchEvent(new Event("change", { bubbles: true }));
    }

    drawIdleVisualizer();
  }

  // Initialize
  resizeCanvas();
  window.addEventListener("resize", resizeCanvas);

  recordBtn.addEventListener("click", () => {
    if (state.isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  });

  reRecordBtn.addEventListener("click", resetRecorder);

  drawIdleVisualizer();
  debugLog("recorder setup complete for widget");
}

window.pushInitAudioRecorder = initAudioRecorder;

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAudioRecorder);
} else {
  initAudioRecorder();
}

document.addEventListener("gform/post_render", ((event: CustomEvent) => {
  const formId = event.detail?.formId;
  debugLog("gform/post_render", { formId, audioFormIds: AUDIO_FORM_IDS });
  if (AUDIO_FORM_IDS.includes(Number(formId))) {
    initAudioRecorder();
  }
}) as EventListener);

jQuery(document).ready(function ($) {
  // Toggle text comment
  $(document).on("click", ".js-text-comment", function (e) {
    e.preventDefault();
    const $form = $(this).closest("form");
    const $field = $form.length
      ? $form.find(TEXT_REVIEW_FIELD_SELECTOR).first()
      : $(TEXT_REVIEW_FIELD_SELECTOR).first();

    debugLog("text comment toggle", {
      found: $field.length > 0,
      formId: $form.attr("data-formid") || $form.attr("id"),
    });

    if (!$field.length) return false;

    $field.slideToggle(function () {
      if ($field.find("textarea").is(":visible")) {
        $field.find("textarea").focus();
        trackEvent("Testimonial Text Comment Opened");
      }
    });
    return false;
  });

  // Track form submission - with or without audio
  $(document).on("gform_confirmation_loaded", function (event, formId) {
    debugLog("gform_confirmation_loaded", { formId });

    // Check if this form had an audio recording attached
    // We use a data attribute set when audio is attached since the form may be replaced
    const hasAudio = document.body.getAttribute("data-audio-recording-attached") === "true";

    if (hasAudio) {
      trackEvent("Testimonial Submitted - With Audio");
    } else {
      trackEvent("Testimonial Submitted - Without Audio");
    }

    // Clean up the flag
    document.body.removeAttribute("data-audio-recording-attached");
  });
});
